import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, CheckCircle2, Phone, Mail } from 'lucide-react';
import { useInView } from '../../hooks/useAnimations';
import Button from '../UI/Button';

const CTA = ({ onBookDemo }) => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  const benefits = [
    'No credit card required',
    'Secure',
    'Setup in minutes',
    'Cancel anytime',
  ];

  return (
    <section ref={ref} className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
      </div>

      {/* Floating Elements */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white/10 blur-2xl"
      />
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl"
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-8"
          >
            <Sparkles className="w-4 h-4 text-white" />
            <span className="text-sm text-white/90">Start your 14-day free trial</span>
          </motion.div>

          {/* Headline */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
            Ready to Streamline Your<br />
            <span className="text-white/90">Telecalling Operations?</span>
          </h2>

          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10">
            Transform your sales process and close more deals with TeleValley. Get started today.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Button
              variant="primary"
              size="lg"
              onClick={onBookDemo}
              icon={ArrowRight}
              iconPosition="right"
              className="bg-white text-purple-700 hover:bg-white/90 shadow-xl min-w-[200px]"
            >
              Book a Demo
            </Button>
            {/* <Button
              variant="secondary"
              size="lg"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 min-w-[200px]"
            >
              Start Free Trial
            </Button> */}
          </div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-4 md:gap-6"
          >
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-white/70" />
                <span className="text-sm text-white/80">{benefit}</span>
              </div>
            ))}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 }}
            className="mt-12 pt-8 border-t border-white/20"
          >
            <p className="text-white/60 mb-4">Have questions? We're here to help.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a href="tel:+917066333961" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
                <span>+91 7066333961</span>
              </a>
              {/* <a href="mailto:contact@televalley.com" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
                <span>contact@televalley.com</span>
              </a> */}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTA;